import {
  type ActivatedAddons,
  type BillableItem,
  type EventDayBasedBillableItem,
  type EventDayBasedBillableItemDocument,
  type EventDayBasedBillableItemType,
  type SubscriptionBillableItemTypes,
  subscriptionBillableItemTypes,
} from '@racemap/sdk/schema/billing';
import { AddOns, isAddOn } from '@racemap/sdk/schema/billing/AddOns';
import { StripeProducts } from '@racemap/sdk/schema/billing/StripeProducts';
import { BillableItemTypes } from '@racemap/sdk/schema/user';
import {
  type BillableItemObject,
  EventLoadTypes,
  type Load,
} from '@racemap/utilities/types/events';
import { type Immutable, castDraft } from 'immer';
import { DateTime } from 'luxon';
import type Stripe from 'stripe';
import { type PRICING_VERSIONS, PRICING_VERSION_TABLE } from '../consts/billing';
import { AuthorizationStates, EventTypes } from '../consts/events';
import type { ObjectId } from '../types/utils';
import {
  type EventCycleInfos,
  getEventCyclesOfTimeRange,
} from './billing/getEventCyclesOfTimeRange';
import { sortBillableItems } from './billing/sortBillableItems';

export function formatTimeKey(timestamp: number | Date): string {
  if (typeof timestamp === 'number') {
    return DateTime.fromMillis(timestamp, { zone: 'utc' }).toFormat('yyyy-MM-dd');
  }
  return DateTime.fromJSDate(timestamp).toUTC().toFormat('yyyy-MM-dd');
}

export function isCloseMonthChange(): boolean {
  const now = DateTime.utc();
  const startOfMonth = DateTime.utc().startOf('month');
  const endOfMonth = DateTime.utc().endOf('month');
  const THRESHOLD_MINUTES = 5;

  // as a result of an issue at the billing where we overwrite the usage exactly at the change of the month we skip this period for the sync to stripe
  if (
    now < startOfMonth.plus({ minutes: THRESHOLD_MINUTES }) ||
    now > endOfMonth.minus({ minutes: THRESHOLD_MINUTES })
  ) {
    return true;
  }
  return false;
}

// function evaluate, that the time key starts a new billing cycle
// and dont belong to the last billing cycle
export function checkForRunningBillingCycle(
  billableEvents: Array<EventDayBasedBillableItem>,
  timeKey: string,
  daysPerBillingCycle = getCurrentNumberOfDaysOfEventCycle(),
) {
  const twoBillingCyclesBefore = DateTime.fromFormat(timeKey, 'yyyy-MM-dd', { zone: 'utc' }).minus({
    days: 2 * daysPerBillingCycle,
  });

  const oneBillingCycleBefore = DateTime.fromFormat(timeKey, 'yyyy-MM-dd', { zone: 'utc' }).minus({
    days: 1 * daysPerBillingCycle,
  });

  const baseTimeKeyValue = DateTime.fromFormat(timeKey, 'yyyy-MM-dd', { zone: 'utc' });
  const sortedBillableEvents = sortBillableItems(billableEvents, 'asc');
  const olderBillableEvents = sortedBillableEvents.filter(
    (billableEvent) =>
      DateTime.fromFormat(billableEvent.timeKey, 'yyyy-MM-dd', { zone: 'utc' }) <= baseTimeKeyValue,
  );

  let lastRunningBillingCycleStartDate: null | Date = null;
  for (const [index, billableEvent] of olderBillableEvents.entries()) {
    const billableEventTimestamp = DateTime.fromFormat(billableEvent.timeKey, 'yyyy-MM-dd', {
      zone: 'utc',
    });
    // events, that are older then 2 times billing cycle are not relevant
    if (billableEventTimestamp < twoBillingCyclesBefore) continue;
    // first event, means is no running billing cycle
    if (+billableEventTimestamp === +baseTimeKeyValue && index === olderBillableEvents.length - 1)
      return false;

    if (lastRunningBillingCycleStartDate == null) {
      // if the last event was in the last billing cycle, the current event is not the first in the billing cycle
      if (billableEventTimestamp.valueOf() > oneBillingCycleBefore.valueOf()) return true;

      lastRunningBillingCycleStartDate = DateTime.fromFormat(billableEvent.timeKey, 'yyyy-MM-dd', {
        zone: 'utc',
      }).toJSDate();
    } else {
      const endOfLastCycle = DateTime.fromJSDate(lastRunningBillingCycleStartDate)
        .plus({ days: daysPerBillingCycle })
        .toMillis();

      // if the current event is after the event end of the last billing cycle, the current event is the first in the billing cycle
      if (billableEventTimestamp.valueOf() > endOfLastCycle) {
        return true;
      }
    }
  }

  return false;
}

export interface EventCycle {
  startDate: Date;
  endDate: Date;
  startTimeKey: string;
  endTimeKey: string;
  eventId: ObjectId;
  billingEvents: Array<EventDayBasedBillableItem>;
  activatedAddons: ActivatedAddons;
}

export type CostlyAddons =
  | StripeProducts.API_ADDON
  | StripeProducts.MONITOR_ADDON
  | StripeProducts.SPONSOR_ADDON
  | StripeProducts.TIMING_ADDON
  | StripeProducts.HIGH_RESOLUTION_ADDON;
interface EventCycleProducts extends Partial<Record<CostlyAddons, number>> {
  [StripeProducts.MAP]: number;
  [StripeProducts.SAFEMAP]: number;
}

function isCostlyAddOn(addOn: AddOns): boolean {
  return [
    AddOns.API,
    AddOns.MONITOR,
    AddOns.SPONSOR,
    AddOns.TIMING,
    AddOns.HIGH_RESOLUTION,
  ].includes(addOn);
}

const mappingAddonToProduct: Partial<Record<AddOns, CostlyAddons>> = {
  [AddOns.API]: StripeProducts.API_ADDON,
  [AddOns.MONITOR]: StripeProducts.MONITOR_ADDON,
  [AddOns.SPONSOR]: StripeProducts.SPONSOR_ADDON,
  [AddOns.TIMING]: StripeProducts.TIMING_ADDON,
  [AddOns.HIGH_RESOLUTION]: StripeProducts.HIGH_RESOLUTION_ADDON,
};

/**
 * Calculates the products for event cycles based on billing events and cycle duration.
 *
 * @param billingEvents - Array of event day-based billable items to process
 * @param daysPerBillingCycle - Number of days in each billing cycle
 * @param basePricePaid - Whether the base price has been paid, defaults to false
 * @param eventType - Type of event to process, defaults to EventTypes.REGULAR
 * @param userCredits - Array of user credits to consider, defaults to an empty array
 * @returns EventCycleProducts containing the calculated products for the billing cycles
 */
export function getProductsOfEventCycles(
  billingEvents: Array<Immutable<EventDayBasedBillableItem> | EventDayBasedBillableItemDocument>,
  daysPerBillingCycle: number,
  basePricePaid = false,
  eventType: EventTypes = EventTypes.REGULAR,
): EventCycleProducts {
  const billingCycles = getEventCyclesOfTimeRange(billingEvents, daysPerBillingCycle);
  return getProductsOfBillingCycles(billingCycles, basePricePaid, eventType);
}

function getDefaultProductsOfBillingCycle(): EventCycleProducts {
  return {
    [StripeProducts.MAP]: 0,
    [StripeProducts.TIMING_ADDON]: 0,
    [StripeProducts.API_ADDON]: 0,
    [StripeProducts.MONITOR_ADDON]: 0,
    [StripeProducts.SPONSOR_ADDON]: 0,
    [StripeProducts.HIGH_RESOLUTION_ADDON]: 0,
    [StripeProducts.SAFEMAP]: 0,
  };
}

export function getBasePriceOfEventType(eventType: EventTypes): BillableItemTypes | null {
  switch (eventType) {
    case EventTypes.SAFEMAP:
      return BillableItemTypes.SAFEMAP_BASE_PRICE;
    case EventTypes.REGULAR:
    case EventTypes.STAGE:
    case EventTypes.CONTEST:
      return BillableItemTypes.BASE_PRICE;
    case EventTypes.STAGE_GROUP:
    case EventTypes.CONTEST_GROUP:
      return null;
    default:
      throw new Error(`No base price for event type ${eventType}`);
  }
}

function getMapStripeProductOfEventType(
  eventType: EventTypes,
): StripeProducts.MAP | StripeProducts.SAFEMAP | null {
  switch (eventType) {
    case EventTypes.SAFEMAP:
      return StripeProducts.SAFEMAP;
    case EventTypes.REGULAR:
      return StripeProducts.MAP;
    default:
      return null;
  }
}

/**
 * Calculates the total product quantities across multiple billing cycles.
 *
 * @param billingCycles - Collection of event cycle information containing billing events and activated add-ons
 * @param basePricePaid - Whether the base price has already been paid. If true, reduces the main product count by 1
 * @param eventType - Type of event to determine the stripe product for the base price
 * @returns Object containing the aggregated product quantities for all billing cycles
 *
 * @example
 * ```typescript
 * const cycles = { eventCycles: [...] };
 * const products = getProductsOfBillingCycles(cycles, true, EventTypes.REGULAR);
 * ```
 */
export function getProductsOfBillingCycles(
  billingCycles: EventCycleInfos,
  basePricePaid = false,
  eventType: EventTypes = EventTypes.REGULAR,
): EventCycleProducts {
  const products = getDefaultProductsOfBillingCycle();
  const mapStripeProduct = getMapStripeProductOfEventType(eventType);

  for (const billingCycle of billingCycles.eventCycles) {
    const productsOfCycle = getDefaultProductsOfBillingCycle();
    if (mapStripeProduct != null) productsOfCycle[mapStripeProduct] = 1;

    for (const billingEvent of billingCycle.billingEvents) {
      for (const [addOn, value] of Object.entries(billingEvent.activatedAddons || {})) {
        if (isAddOn(addOn) && isCostlyAddOn(addOn)) {
          const product = mappingAddonToProduct[addOn];
          if (product == null) continue;

          productsOfCycle[product] = value;
        }
      }
    }

    for (const [key, value] of Object.entries(productsOfCycle)) {
      products[key as CostlyAddons] += value;
    }
  }

  if (basePricePaid && mapStripeProduct != null) {
    products[mapStripeProduct] = Math.max(products[mapStripeProduct] - 1, 0);
  }

  return products;
}

const CountBillingItemTypes = [
  StripeProducts.KEY,
  StripeProducts.PAGE_VIEW,
  StripeProducts.STARTER,
  StripeProducts.GPS_DEVICE,
  StripeProducts.TRANSPONDER,
  StripeProducts.MAP_OR_MONITOR_LOAD,
  StripeProducts.API_OR_LEADERBOARD_LOAD,
] as const;
type CountBillingItemType = (typeof CountBillingItemTypes)[number];
type CountBillingProducts = Partial<Record<StripeProducts, number>>;

export const isCountBillableItemType = (type: string): type is CountBillingItemType => {
  return CountBillingItemTypes.includes(type as CountBillingItemType);
};

export const getBillableItemOfDeviceClass = (
  deviceClass: 'gps_device' | 'transponder' | null,
): BillableItemTypes => {
  if (deviceClass == null) return BillableItemTypes.STARTER;
  if (deviceClass === 'gps_device') return BillableItemTypes.GPS_DEVICE;
  if (deviceClass === 'transponder') return BillableItemTypes.TRANSPONDER;
  throw new Error('Invalid device class');
};

export const getBillableItemTypeOfStripeProduct = (
  product: StripeProducts,
): BillableItemTypes | null => {
  switch (product) {
    case StripeProducts.KEY:
      return BillableItemTypes.KEY;
    case StripeProducts.PAGE_VIEW:
      return BillableItemTypes.PAGE_VIEW;
    case StripeProducts.STARTER:
      return BillableItemTypes.STARTER;
    case StripeProducts.GPS_DEVICE:
      return BillableItemTypes.GPS_DEVICE;
    case StripeProducts.TRANSPONDER:
      return BillableItemTypes.TRANSPONDER;
    case StripeProducts.CUSTOM_APP:
      return BillableItemTypes.APP;
    case StripeProducts.TRACKER_MANAGEMENT:
      return BillableItemTypes.TRACKER_MANAGEMENT;
    case StripeProducts.SMS:
      return BillableItemTypes.SMS;
    case StripeProducts.DATA_USAGE_ZONE_1:
      return BillableItemTypes.DATA_USAGE_ZONE_1;
    case StripeProducts.DATA_USAGE_ZONE_2:
      return BillableItemTypes.DATA_USAGE_ZONE_2;
    case StripeProducts.DATA_USAGE_ZONE_3:
      return BillableItemTypes.DATA_USAGE_ZONE_3;
    case StripeProducts.BASE_PRICE:
      return BillableItemTypes.BASE_PRICE;
    case StripeProducts.API_ADDON:
      return BillableItemTypes.DATA_FEED;
    case StripeProducts.MONITOR_ADDON:
      return BillableItemTypes.MONITOR;
    case StripeProducts.SPONSOR_ADDON:
      return BillableItemTypes.SPONSOR;
    case StripeProducts.TIMING_ADDON:
      return BillableItemTypes.TIMING;
    case StripeProducts.MAP:
      return BillableItemTypes.EVENT_CYCLE;
    case StripeProducts.SAFEMAP_BASE_PRICE:
      return BillableItemTypes.SAFEMAP_BASE_PRICE;
    case StripeProducts.SIM_CARD_ACTIVATION:
      return BillableItemTypes.SIM_CARD;
    case StripeProducts.HIGH_RESOLUTION_ADDON:
      return BillableItemTypes.HIGH_RESOLUTION;
    default:
      return null;
  }
};

export function getRegisteredCountProducts(
  billingItems: Immutable<Array<BillableItem>>,
): CountBillingProducts {
  return billingItems.reduce((acc, item) => {
    const stripeProduct = getStripeProductOfBillableItemType(item.type);
    if (stripeProduct == null) return acc;

    if (isCountBillableItemType(stripeProduct)) {
      acc[stripeProduct] = (acc[stripeProduct] || 0) + 1;
    }
    return acc;
  }, {} as CountBillingProducts);
}

export const getStripeProductOfBillableItemType = (
  type: BillableItemTypes,
): StripeProducts | null => {
  switch (type) {
    case BillableItemTypes.KEY:
      return StripeProducts.KEY;
    case BillableItemTypes.PAGE_VIEW:
      return StripeProducts.PAGE_VIEW;
    case BillableItemTypes.STARTER:
      return StripeProducts.STARTER;
    case BillableItemTypes.GPS_DEVICE:
      return StripeProducts.GPS_DEVICE;
    case BillableItemTypes.TRANSPONDER:
      return StripeProducts.TRANSPONDER;
    case BillableItemTypes.APP:
      return StripeProducts.CUSTOM_APP;
    case BillableItemTypes.TRACKER_MANAGEMENT:
      return StripeProducts.TRACKER_MANAGEMENT;
    case BillableItemTypes.SMS:
      return StripeProducts.SMS;
    case BillableItemTypes.DATA_USAGE_ZONE_1:
    case BillableItemTypes.DATA_USAGE_ZONE_1_V6_SIM:
      return StripeProducts.DATA_USAGE_ZONE_1;
    case BillableItemTypes.DATA_USAGE_ZONE_2:
      return StripeProducts.DATA_USAGE_ZONE_2;
    case BillableItemTypes.DATA_USAGE_ZONE_3:
      return StripeProducts.DATA_USAGE_ZONE_3;
    case BillableItemTypes.BASE_PRICE:
      return StripeProducts.BASE_PRICE;
    case BillableItemTypes.DATA_FEED:
      return StripeProducts.API_ADDON;
    case BillableItemTypes.MONITOR:
      return StripeProducts.MONITOR_ADDON;
    case BillableItemTypes.SPONSOR:
      return StripeProducts.SPONSOR_ADDON;
    case BillableItemTypes.TIMING:
      return StripeProducts.TIMING_ADDON;
    case BillableItemTypes.EVENT_CYCLE:
      return StripeProducts.MAP;
    case BillableItemTypes.SAFEMAP_BASE_PRICE:
      return StripeProducts.SAFEMAP_BASE_PRICE;
    case BillableItemTypes.SIM_CARD:
      return StripeProducts.SIM_CARD_ACTIVATION;
    case BillableItemTypes.HIGH_RESOLUTION:
      return StripeProducts.HIGH_RESOLUTION_ADDON;
    default:
      return null;
  }
};

const MapOrMonitorLoadTypes: Array<EventLoadTypes> = [
  EventLoadTypes.MAP_LOADS,
  EventLoadTypes.MONITOR_LOADS,
];

export const isMapOrMonitorLoad = (load: Load): boolean => {
  if (MapOrMonitorLoadTypes.includes(load.type) || load.type === null) return true;
  return false;
};

const ApiOrLeaderboardLoadTypes: Array<EventLoadTypes> = [
  EventLoadTypes.CURRENT_LOADS,
  EventLoadTypes.DISTANCE_LOADS,
  EventLoadTypes.RANKS_LOADS,
  EventLoadTypes.TIMES_ID_LOADS,
  EventLoadTypes.TIMES_LOADS,
  EventLoadTypes.TIMING_LOADS,
];

export const isApiOrLeaderboardLoad = (load: Load): boolean => {
  if (ApiOrLeaderboardLoadTypes.includes(load.type)) return true;
  return false;
};

export const aggregateLoadProductCounts = (
  loads: Immutable<Array<Load>>,
): {
  [StripeProducts.MAP_OR_MONITOR_LOAD]: number;
  [StripeProducts.API_OR_LEADERBOARD_LOAD]: number;
} => {
  const loadProducts = {
    [StripeProducts.MAP_OR_MONITOR_LOAD]: 0,
    [StripeProducts.API_OR_LEADERBOARD_LOAD]: 0,
  };

  loads.forEach((load) => {
    if (isMapOrMonitorLoad(load)) {
      loadProducts[StripeProducts.MAP_OR_MONITOR_LOAD] += load.count;
    } else if (isApiOrLeaderboardLoad(load)) {
      loadProducts[StripeProducts.API_OR_LEADERBOARD_LOAD] += load.count;
    }
  });

  return loadProducts;
};

export function calculateNumberOfEventCycles(
  billableEventDays: Array<EventDayBasedBillableItem> | Immutable<Array<EventDayBasedBillableItem>>,
  daysPerBillingCycle: number,
  startDate?: Date,
  endDate?: Date,
) {
  return getEventCyclesOfTimeRange(
    castDraft(billableEventDays),
    daysPerBillingCycle,
    startDate,
    endDate,
  ).eventCycles.length;
}

export function isBillableItem(item: unknown): item is BillableItem {
  return (
    typeof item === 'object' &&
    item !== null &&
    'type' in item &&
    'time' in item &&
    'timeKey' in item &&
    typeof item.type === 'string' &&
    Object.values(BillableItemTypes).includes(item.type as BillableItemTypes)
  );
}

export function isEventLoadType(type: string): type is EventLoadTypes {
  return Object.values(EventLoadTypes).includes(type as EventLoadTypes);
}

export function isBillableItemType(type: string): type is BillableItemTypes {
  return Object.values(BillableItemTypes).includes(type as BillableItemTypes);
}

export function isEventLoadTypeKey(type: string): type is keyof typeof EventLoadTypes {
  return Object.keys(EventLoadTypes).includes(type);
}

export function isEventDayBasedBillableItem(item: unknown): item is EventDayBasedBillableItem;
export function isEventDayBasedBillableItem(
  item: Immutable<unknown>,
): item is Immutable<EventDayBasedBillableItem>;
export function isEventDayBasedBillableItem(
  item: unknown | Immutable<unknown>,
): item is EventDayBasedBillableItem | Immutable<EventDayBasedBillableItem> {
  return isBillableItem(item) && item.type === BillableItemTypes.EVENT_DAY;
}

export function isSubscriptionItem(itemType: unknown): itemType is SubscriptionBillableItemTypes {
  return (
    typeof itemType === 'string' &&
    isBillableItemType(itemType) &&
    subscriptionBillableItemTypes.includes(itemType as SubscriptionBillableItemTypes)
  );
}

export function isStripeProduct(product: unknown): product is StripeProducts {
  return (
    typeof product === 'string' &&
    Array.from(Object.values(StripeProducts)).includes(product as StripeProducts)
  );
}

export function getCurrentPricingVersion(referenceDate = DateTime.utc()): PRICING_VERSIONS {
  for (const [version, { from, to }] of Object.entries(PRICING_VERSION_TABLE)) {
    if (referenceDate >= from && (to == null || referenceDate < to)) {
      return version as PRICING_VERSIONS;
    }
  }

  throw new Error('No pricing version found for the given reference date');
}

export function getNumberOfFreeItems(
  product: StripeProducts,
  priceList: Record<StripeProducts, Stripe.Price>,
): number {
  const price = priceList[product];
  if (price == null || price.billing_scheme !== 'tiered') return 0;

  const packageSize = Number.parseInt(price.metadata?.packageSize || '1');
  const freeTier = price.tiers?.find(
    (tier) => tier.unit_amount === 0 && (tier.flat_amount == null || tier.flat_amount === 0),
  );
  if (freeTier == null) return 0;

  return (freeTier.up_to || Number.POSITIVE_INFINITY) * packageSize;
}

export function getCurrentNumberOfDaysOfEventCycle(referenceDate = DateTime.utc()): number {
  return PRICING_VERSION_TABLE[getCurrentPricingVersion(referenceDate)].numDaysEventCycle;
}

export function getPricingRulesOfEvent(event: {
  payments: { invariants: { pricingVersion: PRICING_VERSIONS } };
  authorization: AuthorizationStates;
}): { from: DateTime; to: DateTime | null; numDaysEventCycle: number } {
  const pricingVersion = event.payments.invariants.pricingVersion;
  const currentPricing = getCurrentPricingVersion();
  const currentPricingRules = PRICING_VERSION_TABLE[currentPricing];

  if (!(pricingVersion in PRICING_VERSION_TABLE)) {
    throw new Error(
      `No pricing version found for the given event. Pricing version ${pricingVersion} is not valid.`,
    );
  }

  // if the event is unpaid but the new pricing already started, we force the new pricing
  if (
    event.authorization === AuthorizationStates.NONE &&
    currentPricingRules.from < DateTime.utc()
  ) {
    return currentPricingRules;
  }

  return PRICING_VERSION_TABLE[pricingVersion];
}

export const costlyAddons = [
  AddOns.API,
  AddOns.MONITOR,
  AddOns.SPONSOR,
  AddOns.TIMING,
  AddOns.HIGH_RESOLUTION,
] as const;
export const billableItemTypesOfAddons: ReadonlyArray<BillableItemTypes> = [
  BillableItemTypes.DATA_FEED,
  BillableItemTypes.MONITOR,
  BillableItemTypes.SPONSOR,
  BillableItemTypes.TIMING,
  BillableItemTypes.HIGH_RESOLUTION,
] as const;
export const addOnBillableItemMapping: Record<
  (typeof costlyAddons)[number],
  EventDayBasedBillableItemType
> = {
  [AddOns.API]: BillableItemTypes.DATA_FEED,
  [AddOns.MONITOR]: BillableItemTypes.MONITOR,
  [AddOns.SPONSOR]: BillableItemTypes.SPONSOR,
  [AddOns.TIMING]: BillableItemTypes.TIMING,
  [AddOns.HIGH_RESOLUTION]: BillableItemTypes.HIGH_RESOLUTION,
};
export const billableItemAddonMapping: Record<
  EventDayBasedBillableItemType,
  (typeof costlyAddons)[number]
> = {
  [BillableItemTypes.DATA_FEED]: AddOns.API,
  [BillableItemTypes.MONITOR]: AddOns.MONITOR,
  [BillableItemTypes.SPONSOR]: AddOns.SPONSOR,
  [BillableItemTypes.TIMING]: AddOns.TIMING,
  [BillableItemTypes.HIGH_RESOLUTION]: AddOns.HIGH_RESOLUTION,
};

export const holdsAllBillingItemTypes = (
  items: Array<BillableItem | BillableItemObject>,
  types: Array<BillableItemTypes>,
): boolean => {
  for (const type of types) {
    if (billableItemTypesOfAddons.includes(type)) {
      const addon = billableItemAddonMapping[type as EventDayBasedBillableItemType];
      if (addon == null) return false;

      if (
        !items.some(
          (item) =>
            isEventDayBasedBillableItem(item) &&
            item.activatedAddons?.[addon] != null &&
            (item.activatedAddons?.[addon] || 0) > 0,
        )
      )
        return false;
    } else if (!items.some((item) => item.type === type)) return false;
  }
  return true;
};
